import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { apiDocCodeBg, apiDocCodeText, gray40, gray80, gray100 } from '../../../constants/colors';
import { regularSm, regularXs, sectionTitle } from '../../../constants/location';
import {
  apiKeyPrefix,
  apiRequestLimits,
  OCR_UPLOAD_MAX_TOTAL_PAGES,
  OCR_UPLOAD_MAX_PAGES_PER_FILE,
} from '../../../constants/enum';

interface Props {
  environmentHost: string;
}

const BYTES_PER_MB = 1024 * 1024;

const FILE_BYTES_MAX_MB = apiRequestLimits.fileBytesMax / BYTES_PER_MB;
const TOTAL_BYTES_MAX_MB = apiRequestLimits.totalBytesMax / BYTES_PER_MB;

const ENDPOINT_PARAMS: Array<{
  field: string;
  type: string;
  required: string;
  description: string;
}> = [
  {
    field: 'pattern_id',
    type: 'string',
    required: '任意',
    description: '読取パターン ID。省略時は自動仕分けキューに投入されます。',
  },
  {
    field: 'files',
    type: 'array',
    required: '必須',
    description: `アップロード対象ファイル。最大 ${apiRequestLimits.filesPerRequest} 件。`,
  },
  {
    field: 'files[].file_name',
    type: 'string',
    required: '必須',
    description: 'ファイル名 (拡張子付き)。',
  },
  {
    field: 'files[].mime_type',
    type: 'string',
    required: '必須',
    description: 'application/pdf / image/png / image/jpeg のいずれか。',
  },
  {
    field: 'files[].data',
    type: 'string',
    required: '必須',
    description: 'ファイル内容を Base64 エンコードした文字列。',
  },
  {
    field: 'processing_type',
    type: 'number',
    required: '任意',
    description:
      '1: ページごとに 1 結果 / 2: ファイル単位で 1 結果。省略時は、pattern_id を指定した場合はそのパターンの設定に従い、指定しない場合はページごと (1) で処理されます。',
  },
];

const ERROR_CODES: Array<{ code: string; http: string; condition: string }> = [
  {
    code: 'INVALID_API_KEY',
    http: '401',
    condition: 'ヘッダー欠如 / 無効化済 / 期限切れ / ハッシュ不一致',
  },
  { code: 'IP_NOT_ALLOWED', http: '403', condition: 'IP ホワイトリスト外の送信元' },
  {
    code: 'API_FEATURE_DISABLED',
    http: '403',
    condition: '企業の api_enabled = false / 利用開始日前',
  },
  {
    code: 'INVALID_REQUEST',
    http: '400',
    condition: 'バリデーション失敗（必須欠落・型不正・上限超過）',
  },
  { code: 'UNSUPPORTED_FILE_TYPE', http: '400', condition: 'サポート外 MIME' },
  {
    code: 'FILE_TOO_LARGE',
    http: '413',
    condition: `単一ファイルが ${FILE_BYTES_MAX_MB} MB 超、または合計 ${TOTAL_BYTES_MAX_MB} MB 超`,
  },
  { code: 'CREDIT_INSUFFICIENT', http: '402', condition: 'OCR クレジット残高不足' },
  {
    code: 'IDEMPOTENT_CONFLICT',
    http: '409',
    condition: '同じ Idempotency-Key で異なる本文が送信された',
  },
  { code: 'INTERNAL_ERROR', http: '500', condition: 'サーバー内部エラー' },
];

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  intro: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  pageTitle: {
    ...sectionTitle,
  },
  introText: {
    ...regularSm,
    color: gray80,
  },
  section: {
    border: `1px solid ${gray40}`,
    borderRadius: '4px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  sectionTitle: {
    color: gray100,
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  sectionText: {
    ...regularSm,
    color: gray100,
  },
  codeBox: {
    backgroundColor: apiDocCodeBg,
    color: apiDocCodeText,
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '13px',
    padding: '16px',
    overflowX: 'auto',
    whiteSpace: 'pre',
  },
  caption: {
    ...regularXs,
    color: gray80,
  },
  tableWrapper: {
    overflowX: 'auto',
    width: '100%',
  },
  table: {
    width: '100%',
  },
  tableHeadRow: {
    height: '48px',
  },
  tableHeadText: {
    ...regularSm,
    color: gray80,
    padding: '0 16px',
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
  },
  tableDataRow: {
    height: '48px',
  },
  tableDataText: {
    ...regularSm,
    color: gray100,
    padding: '0 16px',
    textAlign: 'left' as const,
  },
};

const ApiReference: React.FC<Props> = ({ environmentHost }) => {
  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.intro}>
        <Typography sx={styles.pageTitle}>API リファレンス（v1）</Typography>
        <Typography sx={styles.introText}>
          現在は <code>POST /v1/ocr</code> を提供しています。
        </Typography>
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.sectionTitle}>エンドポイント</Typography>
        <Box sx={styles.codeBox}>
          {`POST ${environmentHost}/v1/ocr
Content-Type: application/json
X-GenDrive-Api-Key: ${apiKeyPrefix.live}xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
Idempotency-Key: <任意・冪等性キー>`}
        </Box>
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.sectionTitle}>リクエストボディ</Typography>
        <Box sx={styles.tableWrapper}>
          <Table sx={styles.table}>
            <TableHead>
              <TableRow sx={styles.tableHeadRow}>
                <TableCell sx={styles.tableHeadText}>フィールド</TableCell>
                <TableCell sx={styles.tableHeadText}>型</TableCell>
                <TableCell sx={styles.tableHeadText}>必須</TableCell>
                <TableCell sx={styles.tableHeadText}>説明</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ENDPOINT_PARAMS.map(p => (
                <TableRow key={p.field} sx={styles.tableDataRow}>
                  <TableCell sx={styles.tableDataText}>
                    <code>{p.field}</code>
                  </TableCell>
                  <TableCell sx={styles.tableDataText}>{p.type}</TableCell>
                  <TableCell sx={styles.tableDataText}>{p.required}</TableCell>
                  <TableCell sx={styles.tableDataText}>{p.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Typography sx={styles.caption}>
          制限値: 1 リクエスト {apiRequestLimits.filesPerRequest} ファイルまで / 1 ファイル{' '}
          {FILE_BYTES_MAX_MB} MB まで（Base64 デコード後）/ ファイル合計 {TOTAL_BYTES_MAX_MB} MB
          まで / 合計 {OCR_UPLOAD_MAX_TOTAL_PAGES} ページまで（画像は 1 ページ換算）/ 1 PDF{' '}
          {OCR_UPLOAD_MAX_PAGES_PER_FILE} ページまで（processing_type=2（統合）時）
        </Typography>
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.sectionTitle}>成功時レスポンス (HTTP 202)</Typography>
        <Box sx={styles.codeBox}>
          {`{
  "job_id": "job_xxxxxxxxxxxxxxxxxxxx",
  "results": [
    {
      "file_index": 0,
      "file_name": "invoice.pdf",
      "result_ids": ["xxxxxxxxxxxxxxxxxxxx"]
    }
  ],
  "received_at": "2026-05-21T10:15:30.123Z"
}`}
        </Box>
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.sectionTitle}>エラーコード</Typography>
        <Box sx={styles.tableWrapper}>
          <Table sx={styles.table}>
            <TableHead>
              <TableRow sx={styles.tableHeadRow}>
                <TableCell sx={styles.tableHeadText}>コード</TableCell>
                <TableCell sx={styles.tableHeadText}>HTTP</TableCell>
                <TableCell sx={styles.tableHeadText}>発生条件</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ERROR_CODES.map(e => (
                <TableRow key={e.code} sx={styles.tableDataRow}>
                  <TableCell sx={styles.tableDataText}>
                    <code>{e.code}</code>
                  </TableCell>
                  <TableCell sx={styles.tableDataText}>{e.http}</TableCell>
                  <TableCell sx={styles.tableDataText}>{e.condition}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.sectionTitle}>冪等性</Typography>
        <Typography sx={styles.sectionText}>
          <code>Idempotency-Key</code> ヘッダーを指定すると、過去 24
          時間以内に同一本文で同じキーが送信された場合は初回と同じレスポンスを返します。
          本文ハッシュが異なる場合は <code>IDEMPOTENT_CONFLICT (409)</code> を返します。
        </Typography>
      </Box>
    </Box>
  );
};

export default ApiReference;
